import { useGet, usePatch, usePost, usePut } from "@albertine/api-client";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import { format } from "date-fns-tz";
import { useEffect, useState } from "react";
import { type SubmitErrorHandler, type SubmitHandler, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { z } from "zod";
import type { CatalogItem } from "../../common/CatalogItem";
import type { Agent } from "../../common/types/Agent";
import {
    type Booking,
    type BookingUpdatables,
    isValidNumberOfGuests,
} from "../../common/types/Booking";
import type { FirebaseTimestamp } from "../../common/types/FirebaseTimestamp";
import type { Request } from "../../common/types/Request";
import {
    ReservationStatus,
    ReservationStatusMap,
} from "../../common/types/ReservationStatus";
import { excludeUndefined } from "../../common/utils/array.util";
import { bookingToEventDescription } from "../../common/utils/calendar/calendar.util";
import { cdnImage, supportedImageAccept } from "../../common/utils/image.util";
import {
    dateWithShortMonthWithTimeFormat,
    formatDateInTimeZone,
} from "../../common/utils/time.util";
import { isTypeOf } from "../../common/utils/zod.util";
import { EventEmitter } from "../../EventEmitter";
import {
    bookingAttachmentRef,
    supportedAttachmentFileTypes,
} from "../../FirebaseBackend";
import { deleteFile, uploadFile } from "../../FirebaseBackendActions";
import {
    makePrivate,
    makePublic,
    toUpdateBookingApiJson,
    updateBookingPriceDetails,
} from "../../utils/booking.util";
import { randomUUIDFilename } from "../../utils/file.util";
import { takeSupportedImageFiles } from "../../utils/imageUpload.util";
import routes from "../../utils/routes";
import {
    calculateNights,
    gmtDifference,
    timestampFromDateTimeStrings,
} from "../../utils/time.util";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import Asset from "../Common/Asset";
import Button from "../Common/Buttons";
import Dropdown from "../Common/Dropdown";
import {
    type AutofillSelection,
    EditableZonedDateTimeInput,
} from "../Common/EditableDateInput";
import { Flex, FlexInline } from "../Common/Flex";
import { Form } from "../Common/Form/Form";
import Hr from "../Common/Hr";
import Icon, { PictogramIcon } from "../Common/Icon";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Input from "../Common/Input";
import Label from "../Common/Label";
import FullScreenOverlay from "../Common/Overlay/FullScreenOverlay";
import ModalOverlay from "../Common/Overlay/ModalOverlay";
import Paragraph from "../Common/Paragraph";
import PriceInput from "../Common/PriceInput";
import Tag from "../Common/Tag";
import HandleFileUpload from "../HandleFileUpload";
import BookingAttachmentPreview from "./BookingAttachmentPreview";
import "./EditBookingForm.css";
import type { Supplier } from "@albertine/types";
import { Lib } from "albertine-shared-web";
import {
    type CurrencyCode,
    convertPriceToString,
    type Price,
} from "../../common/currency";
import type { BookingAttachment } from "../../common/types/Attachment";
import {
    type BookingOption,
    BookingOptionsAllowedCategory,
    BookingOptionsTextsMap,
    type BookingOptionUpdate,
} from "../../common/types/BookingOption";
import { UrlString } from "../../common/validate";
import { nameWithoutTitle } from "../../utils/members.util";
import AutocompleteDropdown from "../Common/Input.AutocompleteDropdown";
import { TiptapEditor } from "../Common/TiptapEditor";
import Tooltip from "../Common/Tooltip";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import EditBookingImages from "./EditBookingImages";
import { PaymentItem } from "./RequestPayment/PaymentItem";
import { RequestPaymentModal } from "./RequestPayment/RequestPaymentModal";
import { TotalPaymentsRequested } from "./RequestPayment/TotalPaymentsRequested";

export const FormFirebaseTimestamp = (field: string) =>
    z.object(
        {
            nanoseconds: z.number().optional(),
            seconds: z.number().optional(),
            _nanoseconds: z.number().optional(),
            _seconds: z.number().optional(),
        },
        {
            error: `${field} must have a value in confirmed booking`,
        },
    );

const BookingDetailsFormSchema = z
    .object({
        numberOfGuests: z.string().refine((v) => v === "" || isValidNumberOfGuests(v), {
            error: "Invalid number of guests",
        }),
        rooms: z.string().optional().nullable(),
        reference: z.string().optional().nullable(),
        startTime: FormFirebaseTimestamp("Start time"),
        startTimezone: z.string(),
        endTime: FormFirebaseTimestamp("End time"),
        endTimezone: z.string(),
    })
    .refine(
        (data) =>
            fromTimestampToDate(data.startTime).getTime() <=
            fromTimestampToDate(data.endTime).getTime(),
        {
            error: "End time must be after start time",
            path: ["endTime"],
        },
    )
    .refine((data) => fromTimestampToDate(data.startTime).getTime() > Date.now(), {
        error: "Start time must be in the future",
        path: ["startTime"],
    });
type BookingDetailsFormSchema = z.infer<typeof BookingDetailsFormSchema>;

function bookingFormDataToUpdate(formData: BookingDetailsFormSchema): Partial<Booking> {
    return {
        numberOfGuests:
            formData.numberOfGuests === "" ? null : Number(formData.numberOfGuests),
        rooms: formData.rooms,
        reference: formData.reference,
        startTime: formData.startTime,
        startTimezone: formData.startTimezone,
        endTime: formData.endTime,
        endTimezone: formData.endTimezone,
    };
}

function isAccommodation(booking: Booking): boolean {
    return booking.offeringCategory === "accommodation";
}

function startTimeLabel(booking: Booking): string {
    if (isAccommodation(booking)) {
        return "Check in time";
    }
    return "Start time";
}

function endTimeLabel(booking: Booking): string {
    if (isAccommodation(booking)) {
        return "Check out time";
    }
    return "End time";
}

function defaultStartTime(booking: Booking): string {
    if (isAccommodation(booking)) {
        return "14:00";
    }
    return "18:00";
}

function defaultEndTime(booking: Booking): string {
    if (isAccommodation(booking)) {
        return "12:00";
    }
    return "20:00";
}

function autofillOptionTime(
    defaultTime: string,
    date: string,
    time: string | null | undefined,
): AutofillSelection {
    const timestamp = timestampFromDateTimeStrings(date, time, defaultTime);
    return {
        value: "request",
        label: format(timestamp.toDate(), dateWithShortMonthWithTimeFormat),
        timestamp,
        timezone: null,
    };
}

function CalendarEventDetails(props: { booking: Booking }) {
    const { booking } = props;
    const {
        imageURL,
        startTime,
        endTime,
        startTimezone,
        endTimezone,
        address,
        googleUrl,
    } = booking;
    if (!imageURL || !startTime || !startTimezone || !endTime || !endTimezone)
        return null;

    const startDate = fromTimestampToDate(startTime);
    const endDate = fromTimestampToDate(endTime);

    return (
        <ModalOverlay.Content>
            <Flex.Row
                gap="xlarge"
                alignItems="start"
                className="calendar-event-details__content"
            >
                <Flex.Column className="calendar-event-details__image">
                    <Asset
                        src={cdnImage(imageURL || "", {
                            height: 160,
                            width: 160,
                        })}
                    />
                </Flex.Column>
                <Flex.Column gap="large">
                    <Flex.Column gap="small">
                        <h4>{booking.title}</h4>
                        <Label.Medium.N50>Confirmed</Label.Medium.N50>
                    </Flex.Column>
                    <Flex.Row gap="medium" alignItems="start">
                        <Icon.CalendarMonth.Medium />
                        <Flex.Column>
                            <Flex.Row
                                gap="medium"
                                className="calendar-event-details__time"
                            >
                                <Label.Small>Start:</Label.Small>
                                <Label.Small.SemiBold>
                                    {formatDateInTimeZone(startDate, startTimezone)}
                                </Label.Small.SemiBold>
                                <Label.Small.N50>
                                    {startTimezone} (
                                    {gmtDifference(startDate, startTimezone)})
                                </Label.Small.N50>
                            </Flex.Row>
                            <Flex.Row
                                gap="medium"
                                className="calendar-event-details__time"
                            >
                                <Label.Small>End:</Label.Small>
                                <Label.Small.SemiBold>
                                    {formatDateInTimeZone(endDate, endTimezone)}
                                </Label.Small.SemiBold>
                                <Label.Small.N50>
                                    {endTimezone} ({gmtDifference(endDate, endTimezone)}
                                    )
                                </Label.Small.N50>
                            </Flex.Row>
                        </Flex.Column>
                    </Flex.Row>
                    {googleUrl && googleUrl !== "" ? (
                        <a href={googleUrl} target="_blank" rel="noreferrer">
                            <Flex.Row gap="medium" alignItems="start">
                                <Icon.LocationOn.Medium />
                                <Label.Small className="calendar-event-details__address underline">
                                    {address || googleUrl}
                                </Label.Small>
                            </Flex.Row>
                        </a>
                    ) : (
                        address &&
                        address !== "" && (
                            <Flex.Row gap="medium" alignItems="start">
                                <Icon.LocationOn.Medium />
                                <Label.Small className="calendar-event-details__address underline">
                                    {address}
                                </Label.Small>
                            </Flex.Row>
                        )
                    )}
                    <Flex.Row gap="medium" alignItems="start">
                        <Icon.Notes.Medium />
                        <Flex.Row className="calendar-event-details__invite__body-text">
                            {bookingToEventDescription(booking)}
                        </Flex.Row>
                    </Flex.Row>
                </Flex.Column>
            </Flex.Row>
        </ModalOverlay.Content>
    );
}

function ConfirmSendingCalendarInvite(props: {
    mode: "send" | "re-send";
    booking: Booking;
    onClose: () => boolean;
}) {
    const { mode, booking, onClose } = props;
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
    const { mutateAsync: createBookingCalendarEvent } = usePost(
        "createBookingCalendarEvent",
    );
    const { mutateAsync: resendBookingCalendarEvent } = usePost(
        "resendBookingCalendarEvent",
    );
    const { imageURL, startTime, endTime, startTimezone, endTimezone } = booking;

    if (!imageURL || !startTime || !startTimezone || !endTime || !endTimezone)
        return null;

    const sendCalendarInvite = async () => {
        setShowLoadingSpinner(true);
        try {
            if (mode === "re-send") {
                await resendBookingCalendarEvent({
                    param: { bookingId: booking.id },
                    json: { forceNotify: true },
                });
            } else {
                await createBookingCalendarEvent({
                    param: { bookingId: booking.id },
                });
            }
            setShowLoadingSpinner(false);
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    message: "Invite sent",
                    type: "info",
                },
            });
            onClose();
        } catch (error) {
            alert(error);
            setShowLoadingSpinner(false);
        }
    };

    return (
        <ModalOverlay.Base
            title={mode === "send" ? "Send calendar invite" : "Re-send invite"}
            className="calendar-event-details"
        >
            <CalendarEventDetails booking={booking} />
            <ModalOverlay.Footer>
                <Flex.Row gap="large" justifyContent="end">
                    <Button onClick={onClose}>Close</Button>
                    <Button.N10
                        className="center-aligned-label"
                        onClick={sendCalendarInvite}
                        showLoadingSpinner={showLoadingSpinner}
                    >
                        <Icon.EventAvailable.Medium /> Send calendar invite
                    </Button.N10>
                </Flex.Row>
            </ModalOverlay.Footer>
        </ModalOverlay.Base>
    );
}

function ConfirmCancellingCalendarInvite(props: {
    booking: Booking;
    onClose: () => boolean;
}) {
    const { booking, onClose } = props;
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
    const { mutateAsync: changeBookingStatus } = usePost("changeBookingStatus");
    const { imageURL, startTime, endTime, startTimezone, endTimezone } = booking;
    if (!imageURL || !startTime || !startTimezone || !endTime || !endTimezone)
        return null;

    const cancelBooking = async () => {
        setShowLoadingSpinner(true);
        try {
            await changeBookingStatus({
                param: { bookingId: booking.id },
                json: { status: "cancelled" },
            });
            setShowLoadingSpinner(false);
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    message: "Booking cancelled",
                    type: "info",
                },
            });
            onClose();
        } catch (error) {
            alert(error);
            setShowLoadingSpinner(false);
        }
    };

    return (
        <ModalOverlay.Base title="Cancel booking" className="calendar-event-details">
            <CalendarEventDetails booking={booking} />
            <ModalOverlay.Footer>
                <Flex.Row gap="large" justifyContent="end">
                    <Button onClick={onClose}>Keep this booking</Button>
                    <Button.E40
                        onClick={cancelBooking}
                        showLoadingSpinner={showLoadingSpinner}
                    >
                        <Icon.Cancel.Medium /> Cancel booking
                    </Button.E40>
                </Flex.Row>
            </ModalOverlay.Footer>
        </ModalOverlay.Base>
    );
}

const memberPreferredTag = (
    <Tag.Small.P90>
        <FlexInline gap="medium">
            <Icon.ThumbUp.XSmall />
            <span style={{ whiteSpace: "nowrap" }}>Member preferred</span>
        </FlexInline>
    </Tag.Small.P90>
);

const confirmedTag = (
    <Tag.Small.P90>
        <FlexInline gap="medium">
            <Icon.Check.XSmall />
            <span style={{ whiteSpace: "nowrap" }}>Confirmed</span>
        </FlexInline>
    </Tag.Small.P90>
);

function BookingOptions(props: {
    booking: Booking;
    updateThisBooking: (update: Partial<BookingUpdatables>) => void;
    updateThisBookingCurrency: (currency: CurrencyCode) => void;
    setShowChooseBookingOptionOverlay: (show: boolean) => void;
}) {
    const {
        booking,
        updateThisBooking: wrapUpdateThisBooking,
        updateThisBookingCurrency: wrapUpdateThisBookingCurrency,
        setShowChooseBookingOptionOverlay,
    } = props;

    const [isUpdating, setIsUpdating] = useState(false);

    const updateThisBooking = (update: Partial<BookingUpdatables>) => {
        (async () => {
            setIsUpdating(true);
            await wrapUpdateThisBooking(update);
            setIsUpdating(false);
        })();
    };

    const updateThisBookingCurrency = (currency: CurrencyCode) => {
        (async () => {
            setIsUpdating(true);
            await wrapUpdateThisBookingCurrency(currency);
            setIsUpdating(false);
        })();
    };

    const updateThisBookingOptions = (option: BookingOptionUpdate) => {
        const overrideCurrency = (price: Price): { price: Price } | undefined => {
            if (!option.price) return undefined;
            if (price.currency === option.price.currency) return undefined;
            return {
                price: {
                    ...price,
                    currency: option.price.currency,
                },
            };
        };

        const update = {
            ...(booking.price && overrideCurrency(booking.price)),
            ...(option.price &&
                booking.priceCurrency !== option.price.currency && {
                    priceCurrency: option.price.currency,
                }),
            options: (booking.options ?? []).map((previousOption) =>
                previousOption.id === option.id
                    ? {
                          ...previousOption,
                          ...option,
                      }
                    : {
                          ...previousOption,
                          ...(previousOption.price &&
                              overrideCurrency(previousOption.price)),
                      },
            ),
        };
        updateThisBooking(update);
    };

    const moveThisBookingOption = (
        option: Pick<BookingOptionUpdate, "id">,
        direction: "up" | "down",
    ) => {
        const index = (booking.options ?? []).findIndex(
            (findOption) => findOption.id === option.id,
        );
        if (index === -1) return;
        const newIndex = direction === "up" ? index - 1 : index + 1;
        const options = [...(booking.options ?? [])];
        const [movedOption] = options.splice(index, 1);
        options.splice(newIndex, 0, movedOption);
        updateThisBooking({
            options,
        });
    };

    const uploadBookingOptionImages = async (
        option: Pick<BookingOptionUpdate, "id">,
        files: FileList | null,
    ) => {
        try {
            if (!files || files.length === 0) return;
            if (!booking) return;

            const firstFile = files.item(0);
            const validAttachments = takeSupportedImageFiles(
                firstFile ? [firstFile] : [],
            );
            if (validAttachments.length === 0) return;

            const uploads = validAttachments.map((attachment) =>
                uploadFile(
                    attachment,
                    bookingAttachmentRef(
                        randomUUIDFilename(),
                        booking.id,
                        booking.member,
                    ),
                ),
            );
            const uploaded = await Promise.all(uploads);

            await updateThisBookingOptions({
                id: option.id,
                images: [...uploaded],
            });
        } catch (error) {
            window.alert(error);
        }
    };

    const isEmptyOption = (option: BookingOptionUpdate) =>
        !option.title &&
        !option.description &&
        !option.price &&
        (!option.images || option.images.length === 0);

    const removeThisBookingOption = (option: Pick<BookingOptionUpdate, "id">) => {
        updateThisBooking({
            options: (booking.options ?? []).filter(
                (previousOption) => previousOption.id !== option.id,
            ),
        });
    };

    const renameThisBookingOptionImage = async (
        option: Pick<BookingOptionUpdate, "id">,
        image: BookingAttachment,
    ) => {
        const optionToRename = (booking.options ?? []).find(
            (findOption) => findOption.id === option.id,
        );
        if (!optionToRename) return;
        await updateThisBookingOptions({
            id: option.id,
            images: (optionToRename.images ?? []).map((previousImage) => {
                if (previousImage.id === image.id) {
                    return {
                        ...previousImage,
                        filename: image.filename,
                    };
                }
                return previousImage;
            }),
        });
    };

    const removeThisBookingOptionImage = async (
        option: Pick<BookingOptionUpdate, "id">,
        image: BookingAttachment,
    ) => {
        const optionToRemove = (booking.options ?? []).find(
            (findOption) => findOption.id === option.id,
        );
        if (!optionToRemove) return;
        await updateThisBookingOptions({
            id: option.id,
            images: (optionToRemove.images ?? []).filter(
                (previousImage) => previousImage.id !== image.id,
            ),
        });
    };

    const [collapsedOptionsVisible, setCollapsedOptionsVisible] = useState(false);

    const bookingOptions = booking.options || [];

    const bookingOptionCategory = isTypeOf(
        booking.offeringCategory,
        BookingOptionsAllowedCategory,
    )
        ? booking.offeringCategory
        : undefined;

    const optionsTexts = bookingOptionCategory
        ? BookingOptionsTextsMap[bookingOptionCategory]
        : undefined;

    const optionsTitle = optionsTexts?.title || "Sub-options";

    const hasConfirmedBookingOptions = bookingOptions.some((it) => it.confirmed);

    const confirmedBookingOptions = hasConfirmedBookingOptions
        ? bookingOptions.filter((it) => it.confirmed)
        : bookingOptions;
    const collapsedBookingOptions = hasConfirmedBookingOptions
        ? bookingOptions.filter((it) => !it.confirmed)
        : [];

    const moreThanOneBooking = confirmedBookingOptions.length > 1;

    function bookingOptionComponent(option: BookingOption) {
        return (
            <Flex.Column key={option.id} gap="medium">
                <Flex.Row justifyContent="space-between">
                    <h5>{optionsTexts?.optionTitle ?? "Sub-option"}</h5>
                    <FlexInline gap="medium">
                        {hasConfirmedBookingOptions
                            ? option.confirmed && confirmedTag
                            : option.memberPreferred && memberPreferredTag}

                        {moreThanOneBooking && !hasConfirmedBookingOptions && (
                            <>
                                <Button.Ghost
                                    onClick={() => {
                                        moveThisBookingOption(option, "up");
                                    }}
                                    disabled={isUpdating}
                                >
                                    <Tooltip label="Move up">
                                        <Icon.ArrowUpward.Small />
                                    </Tooltip>
                                </Button.Ghost>
                                <Button.Ghost
                                    onClick={() => {
                                        moveThisBookingOption(option, "down");
                                    }}
                                    disabled={isUpdating}
                                >
                                    <Tooltip label="Move down">
                                        <Icon.ArrowDownward.Small />
                                    </Tooltip>
                                </Button.Ghost>
                            </>
                        )}
                        <Button.Ghost
                            onClick={() => {
                                if (isEmptyOption(option)) {
                                    removeThisBookingOption(option);
                                } else if (
                                    window.confirm(
                                        optionsTexts?.deleteButton ?? "Delete option?",
                                    )
                                ) {
                                    removeThisBookingOption(option);
                                }
                            }}
                            disabled={isUpdating}
                        >
                            {moreThanOneBooking ? (
                                <Tooltip label="Delete" placement="top-right">
                                    <Icon.Delete.Small />
                                </Tooltip>
                            ) : (
                                <FlexInline gap="small">
                                    <Label.Small.SemiBold>Delete</Label.Small.SemiBold>
                                    <Icon.Delete.Small />
                                </FlexInline>
                            )}
                        </Button.Ghost>
                    </FlexInline>
                </Flex.Row>
                <Input.TextField
                    label="Option name"
                    value={option.title ?? ""}
                    placeholder={
                        optionsTexts?.optionNamePlaceholder ??
                        "E.g. Junior Suite, VIP hospitality ticket, Morning flight..."
                    }
                    onBlur={(title) => {
                        updateThisBookingOptions({
                            id: option.id,
                            title,
                        });
                    }}
                />
                <Input.TextArea
                    label="Description"
                    value={option.description ?? ""}
                    placeholder={
                        optionsTexts?.optionDescriptionPlaceholder ??
                        "What does the option include? Brief bullets only, no full sentences."
                    }
                    autoResize
                    onBlur={(description) => {
                        updateThisBookingOptions({
                            id: option.id,
                            description,
                        });
                    }}
                />
                <PriceInput
                    defaultCurrency={option.price?.currency ?? booking.priceCurrency}
                    price={option.price}
                    onChange={(price) => {
                        updateThisBookingOptions({
                            id: option.id,
                            price,
                        });
                    }}
                    onChangeCurrency={updateThisBookingCurrency}
                />
                {option.images?.map((image) => (
                    <BookingAttachmentPreview
                        key={image.id}
                        attachment={image}
                        onRename={async (imageRenamed) =>
                            renameThisBookingOptionImage(option, imageRenamed)
                        }
                        onRemove={async (imageToRemove) =>
                            removeThisBookingOptionImage(option, imageToRemove)
                        }
                    />
                ))}
                {(!option.images || option.images.length === 0) && (
                    <HandleFileUpload
                        dragAndDropLabel="Add image..."
                        accept={supportedImageAccept}
                        onUpload={(files) =>
                            void uploadBookingOptionImages(option, files)
                        }
                        className="edit-booking-form__add-option-image"
                    >
                        <Button className="edit-booking-form__add-option-image--button-prevent-click">
                            <Icon.AddPhotoAlternate.Small />
                            Add image
                        </Button>
                    </HandleFileUpload>
                )}
                <hr />
            </Flex.Column>
        );
    }

    const addSubOptionButton = (
        <Button
            onClick={() => {
                updateThisBooking({
                    options: [
                        ...(booking.options ?? []),
                        {
                            id: crypto.randomUUID(),
                            title: "",
                            description: "",
                        },
                    ],
                });
            }}
            showLoadingSpinner={isUpdating}
            disabled={isUpdating}
        >
            <Icon.Add.Medium />
            <Label.Medium.SemiBold>
                {optionsTexts?.addButton ?? "Add sub-option"}
            </Label.Medium.SemiBold>
        </Button>
    );

    return (
        <>
            <Flex.Row justifyContent="space-between">
                <h4>{optionsTitle}</h4>
                {hasConfirmedBookingOptions && (
                    <Button
                        onClick={() => {
                            setShowChooseBookingOptionOverlay(true);
                        }}
                    >
                        Edit confirmed options
                    </Button>
                )}
            </Flex.Row>
            <Paragraph.Medium.Light.N10>
                {optionsTexts?.description ??
                    "Add sub-options when offering multiple variations of the same item, such as hotel room types or event tickets."}
            </Paragraph.Medium.Light.N10>
            {confirmedBookingOptions.map((option) => bookingOptionComponent(option))}
            {collapsedBookingOptions.length > 0 ? (
                <>
                    <Button.Ghost
                        onClick={() => {
                            setCollapsedOptionsVisible(!collapsedOptionsVisible);
                        }}
                        className="edit-booking-form__add-options__collapsed-button"
                    >
                        <Flex.Row justifyContent="space-between">
                            <h4>Proposed {optionsTitle.toLowerCase()}</h4>
                            {collapsedOptionsVisible ? (
                                <Icon.ExpandLess.Medium />
                            ) : (
                                <Icon.ExpandMore.Medium />
                            )}
                        </Flex.Row>
                    </Button.Ghost>
                    {collapsedOptionsVisible && (
                        <Flex.Column
                            gap="medium"
                            className="edit-booking-form__add-options__collapsed"
                        >
                            {collapsedBookingOptions.map((option) =>
                                bookingOptionComponent(option),
                            )}
                        </Flex.Column>
                    )}
                </>
            ) : (
                addSubOptionButton
            )}
        </>
    );
}

function BookingOptionsSelectionOverlay(props: {
    booking: Booking;
    onClose: () => void;
    onConfirm: (selected: string[]) => void;
}) {
    const { booking, onClose, onConfirm } = props;
    const bookingOptions = booking.options ?? [];
    const [selected, setSelected] = useState<string[]>(
        bookingOptions.filter((it) => it.confirmed).map((it) => it.id),
    );

    if (bookingOptions.length === 0) return null;

    function toggleSelected(optionId: string) {
        setSelected((prev) =>
            prev.includes(optionId)
                ? prev.filter((it) => it !== optionId)
                : [...prev, optionId],
        );
    }

    function isSelected(optionId: string): boolean {
        return selected.includes(optionId);
    }

    const canConfirm = selected.length > 0;

    return (
        <ModalOverlay.Base title="Confirm booking" onClose={onClose}>
            <ModalOverlay.Content padding="medium">
                <Flex.Column gap="large">
                    <h3>Which options did you book?</h3>
                    <Paragraph.Medium>
                        Member will see the confirmed option details in their booking
                        confirmation.
                    </Paragraph.Medium>
                    {bookingOptions.map((option) => (
                        <Button.Ghost
                            key={option.id}
                            className={classNames("edit-booking-form__booking-option", {
                                "edit-booking-form__booking-option--selected":
                                    isSelected(option.id),
                            })}
                            onClick={() => {
                                toggleSelected(option.id);
                            }}
                        >
                            <Flex.Row gap="large" alignItems="start">
                                {option.images?.[0] && (
                                    <img
                                        src={option.images[0].url}
                                        alt={option.title}
                                        className="edit-booking-form__booking-option__image"
                                    />
                                )}
                                <Flex.Row gap="large" alignItems="center">
                                    <Flex.Column gap="small">
                                        <Label.Medium.SemiBold>
                                            {option.title}
                                        </Label.Medium.SemiBold>
                                        {option.description && (
                                            <Paragraph.Small className="edit-booking-form__booking-option__description">
                                                {option.description}
                                            </Paragraph.Small>
                                        )}
                                        {option.price && (
                                            <Flex.Column gap="small">
                                                <Label.XSmall.N60>
                                                    Total price:
                                                </Label.XSmall.N60>
                                                <Label.Small>
                                                    {convertPriceToString(option.price)}
                                                </Label.Small>
                                            </Flex.Column>
                                        )}
                                    </Flex.Column>
                                    {option.memberPreferred && memberPreferredTag}
                                    <Input.Checkbox
                                        label=""
                                        checked={isSelected(option.id)}
                                    />
                                </Flex.Row>
                            </Flex.Row>
                        </Button.Ghost>
                    ))}
                </Flex.Column>
            </ModalOverlay.Content>

            <ModalOverlay.Footer>
                <Flex.Row gap="large" justifyContent="end">
                    <Button label="Cancel" onClick={onClose} />
                    <Button.N10
                        label="Confirm"
                        disabled={!canConfirm}
                        onClick={() => {
                            onConfirm(selected);
                        }}
                    />
                </Flex.Row>
            </ModalOverlay.Footer>
        </ModalOverlay.Base>
    );
}

function EditBookingDetails(props: { booking: Booking; onClose: () => boolean }) {
    const { booking, onClose } = props;
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
    const { mutateAsync: patchBooking } = usePatch("updateBooking");
    const { mutateAsync: createBookingCalendarEvent } = usePost(
        "createBookingCalendarEvent",
    );
    const { mutateAsync: resendBookingCalendarEvent } = usePost(
        "resendBookingCalendarEvent",
    );

    const {
        imageURL,
        startTime,
        endTime,
        startTimezone,
        endTimezone,
        rooms,
        reference,
    } = booking;
    const {
        setValue,
        handleSubmit,
        trigger,
        formState: { errors, isValid, defaultValues },
    } = useForm<BookingDetailsFormSchema>({
        defaultValues: {
            numberOfGuests: booking.numberOfGuests?.toString() ?? "",
            rooms: rooms ?? "",
            reference: reference ?? "",
            startTime: startTime ?? undefined,
            startTimezone: startTimezone ?? "",
            endTime: endTime ?? undefined,
            endTimezone: endTimezone ?? "",
        },
        resolver: zodResolver(BookingDetailsFormSchema),
    });

    const [startTimeValue, setStartTimeValue] = useState<FirebaseTimestamp | undefined>(
        defaultValues?.startTime,
    );
    const [startTimezoneValue, setStartTimezoneValue] = useState<string | undefined>(
        defaultValues?.startTimezone,
    );

    const [endTimeValue, setEndTimeValue] = useState<FirebaseTimestamp | undefined>(
        defaultValues?.endTime,
    );
    const [endTimezoneValue, setEndTimezoneValue] = useState<string | undefined>(
        defaultValues?.endTimezone,
    );

    const onInvalid: SubmitErrorHandler<BookingDetailsFormSchema> = () => {
        void trigger();
    };

    if (!imageURL || !startTime || !startTimezone || !endTime || !endTimezone)
        return null;

    const onSubmit: SubmitHandler<BookingDetailsFormSchema> = async (update) => {
        setShowLoadingSpinner(true);
        try {
            await patchBooking({
                param: { bookingId: booking.id },
                json: toUpdateBookingApiJson(bookingFormDataToUpdate(update)),
            });
            if (booking.calendarEventId) {
                // Quiet content update — do not force Google to re-notify attendees.
                await resendBookingCalendarEvent({
                    param: { bookingId: booking.id },
                    json: { forceNotify: false },
                });
            } else {
                await createBookingCalendarEvent({
                    param: { bookingId: booking.id },
                });
            }
            setShowLoadingSpinner(false);
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    message: booking.calendarEventId
                        ? "Calendar invite updated"
                        : "Invite sent",
                    type: "info",
                },
            });
            onClose();
        } catch (error) {
            alert(error);
            setShowLoadingSpinner(false);
        }
    };

    const errorTexts = Object.values(errors)
        .map((it) => it.message)
        .filter(excludeUndefined);
    const accommodation = isAccommodation(booking);
    return (
        <ModalOverlay.Base
            title="Edit booking details"
            className="edit-booking-details"
        >
            <Form.Base
                onSubmit={handleSubmit(onSubmit, onInvalid)}
                className="edit-booking-details__form"
            >
                <ModalOverlay.Content>
                    <Form.Inputs
                        disabled={showLoadingSpinner}
                        className="edit-booking-details__form__inputs"
                    >
                        <Flex.Column gap="large">
                            <Input.TextField
                                label="Number of guests"
                                value={defaultValues?.numberOfGuests ?? ""}
                                isValid={() => !errors.numberOfGuests}
                                trim
                                onChange={(value) => {
                                    setValue("numberOfGuests", value);
                                    void trigger("numberOfGuests");
                                }}
                            />
                            {accommodation && (
                                <Input.TextField
                                    label="Rooms and room types"
                                    placeholder="e.g. 2 double rooms, 1 single room"
                                    value={defaultValues?.rooms ?? ""}
                                    isValid={() => !errors.rooms}
                                    trim
                                    onChange={(value) => {
                                        setValue("rooms", value);
                                        void trigger("rooms");
                                    }}
                                />
                            )}
                            <Input.TextField
                                label="Booking reference"
                                value={defaultValues?.reference ?? ""}
                                isValid={() => !errors.reference}
                                trim
                                onChange={(value) => {
                                    setValue("reference", value);
                                    void trigger("reference");
                                }}
                            />
                            <EditableZonedDateTimeInput
                                className="edit-booking-details__date-time"
                                errorHighlight={
                                    !!errors.startTime || !!errors.startTimezone
                                }
                                dateLabel={startTimeLabel(booking)}
                                value={{
                                    timestamp: startTimeValue,
                                    timezone: startTimezoneValue,
                                }}
                                onChange={({ timestamp, timezone }) => {
                                    if (timestamp) {
                                        setStartTimeValue(timestamp);
                                        setValue("startTime", timestamp);
                                    }

                                    if (timezone) {
                                        setStartTimezoneValue(timezone);
                                        setValue("startTimezone", timezone);
                                    }

                                    void trigger();
                                }}
                            />
                            <EditableZonedDateTimeInput
                                className="edit-booking-details__date-time"
                                dateLabel={endTimeLabel(booking)}
                                errorHighlight={
                                    !!errors.endTime || !!errors.endTimezone
                                }
                                value={{
                                    timestamp: endTimeValue,
                                    timezone: endTimezoneValue,
                                }}
                                onChange={({ timestamp, timezone }) => {
                                    if (timestamp) {
                                        setEndTimeValue(timestamp);
                                        setValue("endTime", timestamp);
                                    }

                                    if (timezone) {
                                        setEndTimezoneValue(timezone);
                                        setValue("endTimezone", timezone);
                                    }

                                    void trigger();
                                }}
                            />
                            {!isValid && <Form.Errors errors={errorTexts} />}
                        </Flex.Column>
                    </Form.Inputs>
                </ModalOverlay.Content>
                <ModalOverlay.Footer>
                    <Flex.Row gap="large" justifyContent="end">
                        <Form.Actions>
                            <Button onClick={onClose}>Discard changes</Button>
                            <Button.N10
                                type="submit"
                                disabled={!isValid || showLoadingSpinner}
                                showLoadingSpinner={showLoadingSpinner}
                            >
                                <Icon.EventAvailable.Small /> Save and update calendar
                                invite
                            </Button.N10>
                        </Form.Actions>
                    </Flex.Row>
                </ModalOverlay.Footer>
            </Form.Base>
        </ModalOverlay.Base>
    );
}
type BookingDetailsPanelProps = {
    booking: Booking;
    request: Request;
    onUpdate: (update: Partial<BookingUpdatables>) => void;
    displayShowChooseBookingOptionOverlay: () => void;
};

type BookingDetailsState = "editable" | "confirmed" | "cancelled";

function getBookingDetailsState(booking: Booking): BookingDetailsState {
    switch (booking.reservationStatus) {
        case "confirmed":
            return booking.calendarEventId ? "confirmed" : "editable";
        case "cancelled":
            return "cancelled";
        default:
            return "editable";
    }
}

const calculateNumberOfNights = (
    start: FirebaseTimestamp | null | undefined,
    end: FirebaseTimestamp | null | undefined,
    timezone: string | null | undefined,
    accommodation: boolean,
): number | null => {
    if (!start || !end || !timezone || !accommodation) return null;
    const checkIn = fromTimestampToDate(start);
    const checkOut = fromTimestampToDate(end);
    return calculateNights(checkIn, checkOut, timezone);
};

function EditableBookingDetails(props: {
    booking: Booking;
    request: Request;
    onUpdate: (update: Partial<BookingUpdatables>) => void;
    displayShowChooseBookingOptionOverlay: () => void;
}) {
    const { booking, request, onUpdate, displayShowChooseBookingOptionOverlay } = props;
    const { mutateAsync: changeBookingStatus } = usePost("changeBookingStatus");
    const {
        startTime,
        endTime,
        startTimezone,
        endTimezone,
        reservationStatus,
        reference,
        rooms,
        numberOfGuests,
        imageURL,
    } = booking;
    const [pax, setPax] = useState(numberOfGuests?.toString() ?? "");
    const [confirmSendInvite, setConfirmSendInvite] = useState(false);
    const accommodation = isAccommodation(booking);

    const startTimeIsInTheFuture =
        startTime && fromTimestampToDate(startTime).getTime() > new Date().getTime();

    const isEndDateValid: () => boolean = () => {
        if (!startTime || !startTimezone) return true;
        if (endTime && endTimezone) {
            return fromTimestampToDate(startTime) <= fromTimestampToDate(endTime);
        }
        return true;
    };

    const canConfirm =
        reservationStatus === "confirmed" &&
        !(!startTime || !endTime || !startTimezone || !endTimezone || !imageURL) &&
        startTimeIsInTheFuture &&
        isEndDateValid();

    const displayConfirmBookingOptionsFirst =
        booking.options && booking.options.length > 0;

    return (
        <Flex.Column gap="large">
            <Dropdown
                label="Booking status"
                entries={Object.entries(ReservationStatusMap)}
                currentValue={reservationStatus}
                onChange={async (value) => {
                    if (!isTypeOf(value, ReservationStatus)) return;

                    if (value === "confirmed" && displayConfirmBookingOptionsFirst) {
                        displayShowChooseBookingOptionOverlay();
                        return;
                    }

                    await changeBookingStatus({
                        param: { bookingId: booking.id },
                        json: { status: value },
                    });
                }}
            />

            {(!reservationStatus || reservationStatus === "unselected") && (
                <h5>Proposed option</h5>
            )}
            {reservationStatus === "confirmed" && startTime !== null && (
                <Tag.Small.P90 className="left-aligned-label">
                    Confirmed bookings are visible to the member
                </Tag.Small.P90>
            )}
            {reservationStatus === "confirmed" && startTime === null && (
                <Tag.Small.E90 className="left-aligned-label">
                    Confirmed bookings without dates are not visible in the app
                </Tag.Small.E90>
            )}

            <Input.TextField
                label="Number of guests"
                value={pax}
                isValid={(value) => value === "" || isValidNumberOfGuests(value)}
                onBlurIsValid={(value) => {
                    setPax(value);
                    if (value !== "") {
                        onUpdate({
                            numberOfGuests: parseInt(value, 10),
                        });
                    } else {
                        onUpdate({
                            numberOfGuests: null,
                        });
                    }
                }}
            />
            {accommodation && (
                <Input.TextField
                    label="Rooms and room types"
                    placeholder="e.g. 2 double rooms, 1 single room"
                    value={rooms ?? ""}
                    onBlur={(value) => {
                        onUpdate({ rooms: value });
                    }}
                />
            )}
            {!!reservationStatus && reservationStatus !== "unselected" && (
                <Input.TextField
                    label="Booking reference"
                    value={reference ?? ""}
                    isValid={(value) => value.trim() !== ""}
                    onBlur={(value) => {
                        onUpdate({ reference: value });
                    }}
                />
            )}
            <EditableZonedDateTimeInput
                dateLabel={startTimeLabel(booking)}
                autofillOptions={
                    request?.startDate
                        ? [
                              autofillOptionTime(
                                  defaultStartTime(booking),
                                  request.startDate,
                                  request.startTime,
                              ),
                          ]
                        : []
                }
                value={{
                    timestamp: startTime,
                    timezone: startTimezone,
                }}
                onChange={async ({ timestamp, timezone }) => {
                    const numberOfNights = calculateNumberOfNights(
                        timestamp,
                        endTime,
                        timezone,
                        accommodation,
                    );
                    await onUpdate({
                        startTime: timestamp,
                        startTimezone: timezone,
                        numberOfNights,
                    });
                }}
            />
            <EditableZonedDateTimeInput
                errorHighlight={!isEndDateValid()}
                dateLabel={endTimeLabel(booking)}
                autofillOptions={
                    request?.endDate
                        ? [
                              autofillOptionTime(
                                  defaultEndTime(booking),
                                  request.endDate,
                                  request.endTime,
                              ),
                          ]
                        : []
                }
                value={{
                    timestamp: endTime,
                    timezone: endTimezone,
                }}
                onChange={async ({ timestamp, timezone }) => {
                    const numberOfNights = calculateNumberOfNights(
                        startTime,
                        timestamp,
                        startTimezone,
                        accommodation,
                    );

                    await onUpdate({
                        endTime: timestamp,
                        endTimezone: timezone,
                        numberOfNights,
                    });
                }}
            />

            {canConfirm && (
                <Flex.Column alignItems="stretch">
                    <Hr />
                    <Button.N10
                        onClick={() => {
                            setConfirmSendInvite(true);
                        }}
                        className="center-aligned-label"
                    >
                        <Icon.EventAvailable.Medium /> Send calendar invite
                    </Button.N10>
                </Flex.Column>
            )}
            {confirmSendInvite && (
                <ConfirmSendingCalendarInvite
                    mode="send"
                    booking={booking}
                    onClose={() => {
                        setConfirmSendInvite(false);
                        return false;
                    }}
                />
            )}
        </Flex.Column>
    );
}

function ReadOnlyBookingDetails(props: { booking: Booking }) {
    const { booking } = props;
    const {
        startTime,
        startTimezone,
        endTime,
        endTimezone,
        numberOfGuests,
        reference,
        rooms,
    } = booking;

    if (!startTime || !endTime || !startTimezone || !endTimezone) return null;

    const startDate = fromTimestampToDate(startTime);
    const endDate = fromTimestampToDate(endTime);

    return (
        <>
            {numberOfGuests && (
                <Flex.Column gap="small">
                    <Label.XSmall.N50>Number of guests</Label.XSmall.N50>
                    <Label.Medium>{numberOfGuests}</Label.Medium>
                </Flex.Column>
            )}
            {rooms && (
                <Flex.Column gap="small">
                    <Label.XSmall.N50>Rooms</Label.XSmall.N50>
                    <Label.Medium>{rooms}</Label.Medium>
                </Flex.Column>
            )}
            {reference && (
                <Flex.Column gap="small">
                    <Label.XSmall.N50>Booking reference</Label.XSmall.N50>
                    <Label.Medium>{reference}</Label.Medium>
                </Flex.Column>
            )}
            <Flex.Column gap="small">
                <Label.XSmall.N50>Start time</Label.XSmall.N50>
                <Label.Medium>
                    {formatDateInTimeZone(startDate, startTimezone)}
                </Label.Medium>
                <Label.Small>
                    {startTimezone}{" "}
                    <Label.Small.N50>
                        ({gmtDifference(startDate, startTimezone)})
                    </Label.Small.N50>
                </Label.Small>
            </Flex.Column>
            <Flex.Column gap="small">
                <Label.XSmall.N50>End time</Label.XSmall.N50>
                <Label.Medium>
                    {formatDateInTimeZone(endDate, endTimezone)}
                </Label.Medium>
                <Label.Small>
                    {endTimezone}{" "}
                    <Label.Small.N50>
                        ({gmtDifference(endDate, endTimezone)})
                    </Label.Small.N50>
                </Label.Small>
            </Flex.Column>
        </>
    );
}

function ConfirmedBookingDetails(props: { booking: Booking }) {
    const { booking } = props;

    const [editBookingDetails, setEditBookingDetails] = useState(false);
    const [confirmCancelBooking, setConfirmCancelBooking] = useState(false);
    const [confirmResendInvite, setConfirmResendInvite] = useState(false);

    return (
        <Flex.Column gap="large">
            <Tag.Small.P90 className="center-aligned-label">
                <Icon.Check.XSmall />
                Confirmed and calendar invite sent
            </Tag.Small.P90>
            <ReadOnlyBookingDetails booking={booking} />
            <Hr />
            <Flex.Row justifyContent="space-evenly" alignItems="stretch">
                <Button
                    className="confirmed-details__edit-button center-aligned-label"
                    onClick={() => {
                        setEditBookingDetails(true);
                    }}
                >
                    <Icon.Edit.Medium /> Edit booking details
                </Button>
            </Flex.Row>
            <Flex.Row justifyContent="space-evenly">
                <Button.Ghost
                    onClick={() => {
                        setConfirmCancelBooking(true);
                    }}
                >
                    <Label.Small.SemiBold.E40 className="center-aligned-label">
                        <Icon.Cancel.Medium /> Cancel booking
                    </Label.Small.SemiBold.E40>
                </Button.Ghost>
                <Button.Ghost
                    onClick={() => {
                        setConfirmResendInvite(true);
                    }}
                >
                    <Label.Small.SemiBold className="center-aligned-label">
                        <Icon.Event.Medium /> Re-send invite
                    </Label.Small.SemiBold>
                </Button.Ghost>
            </Flex.Row>
            {editBookingDetails && (
                <EditBookingDetails
                    booking={booking}
                    onClose={() => {
                        setEditBookingDetails(false);
                        return false;
                    }}
                />
            )}
            {confirmCancelBooking && (
                <ConfirmCancellingCalendarInvite
                    booking={booking}
                    onClose={() => {
                        setConfirmCancelBooking(false);
                        return false;
                    }}
                />
            )}
            {confirmResendInvite && (
                <ConfirmSendingCalendarInvite
                    mode="re-send"
                    booking={booking}
                    onClose={() => {
                        setConfirmResendInvite(false);
                        return false;
                    }}
                />
            )}
        </Flex.Column>
    );
}

function CancelledBookingDetails(props: { booking: Booking }) {
    const { booking } = props;
    const { mutateAsync: changeBookingStatus } = usePost("changeBookingStatus");

    const { reservationStatus } = booking;

    return (
        <Flex.Column gap="large">
            <Dropdown
                label="Booking status"
                entries={Object.entries(ReservationStatusMap)}
                currentValue={reservationStatus}
                onChange={async (value) => {
                    if (!isTypeOf(value, ReservationStatus)) return;
                    if (value === "cancelled") {
                        return;
                    }

                    await changeBookingStatus({
                        param: { bookingId: booking.id },
                        json: { status: value },
                    });
                }}
            />
            <ReadOnlyBookingDetails booking={booking} />
        </Flex.Column>
    );
}

function BookingDetailsPanel(props: BookingDetailsPanelProps) {
    const { booking, request, onUpdate, displayShowChooseBookingOptionOverlay } = props;
    const state = getBookingDetailsState(booking);
    return (
        <Flex.Column gap="large" className="edit-booking-form__right__panel">
            <h4>Booking details</h4>
            {state === "editable" && (
                <EditableBookingDetails
                    booking={booking}
                    request={request}
                    onUpdate={onUpdate}
                    displayShowChooseBookingOptionOverlay={
                        displayShowChooseBookingOptionOverlay
                    }
                />
            )}
            {state === "confirmed" && <ConfirmedBookingDetails booking={booking} />}
            {state === "cancelled" && <CancelledBookingDetails booking={booking} />}
        </Flex.Column>
    );
}

type EditBookingFormProps = {
    booking: Booking;
    request: Request;
    member: MemberDataFirestoreRecord;
    currentAgent: Agent;
    suppliers: Supplier[];
};

export default function EditBookingForm(props: EditBookingFormProps) {
    const { booking, request, member, suppliers } = props;
    const { mutateAsync: patchBooking } = usePatch("updateBooking");
    const { mutateAsync: putBookingAttachments } = usePut("updateBookingAttachments");
    const { mutateAsync: changeBookingStatus } = usePost("changeBookingStatus");
    const { mutateAsync: archiveBookingMutation } = usePost("archiveBooking");

    const [showChooseBookingOptionOverlay, setShowChooseBookingOptionOverlay] =
        useState(false);
    const [showRequestPaymentModal, setShowRequestPaymentModal] = useState(false);
    const history = useHistory();
    const bookingCurrency = booking.price?.currency ?? booking.priceCurrency;

    const {
        data: payments = [],
        isLoading: isLoadingPayments,
        refetch: refetchPayments,
    } = useGet("bookingPayments", { param: { bookingId: booking.id } });

    const { data: memberPaymentPreferences } = useGet("memberPaymentPreferences", {
        param: { memberId: member.id },
    });
    const memberPreferredCurrency =
        memberPaymentPreferences?.preferredCurrency ?? undefined;

    const [uploadingAttachments, setUploadingAttachments] = useState(false);

    const updateThisBooking = (update: Partial<BookingUpdatables>) =>
        patchBooking({
            param: { bookingId: booking.id },
            json: toUpdateBookingApiJson(update),
        });

    const updateThisBookingAttachments = (attachments: BookingAttachment[]) =>
        putBookingAttachments({
            param: { bookingId: booking.id },
            json: { attachments },
        });

    const updateThisBookingCurrency = (currency: CurrencyCode) => {
        void updateThisBooking({
            priceCurrency: currency,
            options: booking.options
                ? booking.options.map((option) => ({
                      ...option,
                      price: option.price ? { ...option.price, currency } : undefined,
                  }))
                : undefined,
        });
    };

    const isImageRequired = () => {
        if (
            booking.reservationStatus === "prebooked" ||
            booking.reservationStatus === "confirmed"
        ) {
            return !booking.imageURL;
        }
        return false;
    };

    const uploadAttachments = async (files: FileList | null) => {
        setUploadingAttachments(true);
        try {
            if (!files || files.length === 0) return;
            if (!booking) return;

            const validAttachments = Array.from(files).filter((file) =>
                supportedAttachmentFileTypes.includes(file.type),
            );

            if (!validAttachments.length) {
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        message: `Couldn't upload file. Please check its file type.`,
                        type: "error",
                    },
                });

                return;
            }

            const uploads = validAttachments.map((attachment) =>
                uploadFile(
                    attachment,
                    bookingAttachmentRef(
                        randomUUIDFilename(),
                        booking.id,
                        booking.member,
                    ),
                ),
            );
            const uploaded = await Promise.all(uploads);

            await updateThisBookingAttachments([
                ...(booking.attachments ?? []),
                ...uploaded.map(makePrivate),
            ]);

            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    message: "Attachment uploaded",
                    type: "info",
                },
            });
        } catch (error) {
            window.alert(error);
        } finally {
            setUploadingAttachments(false);
        }
    };

    const headerRow = (
        <Flex.Row
            gap="small"
            className="edit-booking-form__title"
            alignItems="center"
            justifyContent="space-between"
        >
            <Flex.Row gap="small">
                <h2>Edit option</h2>
                <InlineTextDelimiter />
                <Label.Medium.N50>{request.title}</Label.Medium.N50>
                <InlineTextDelimiter />
                <Label.Medium.N50>{nameWithoutTitle(member)}</Label.Medium.N50>
            </Flex.Row>
            {booking.reviewNeeded ? (
                <Flex.Row gap="large" justifyContent="end">
                    <PictogramIcon.PriorityHigh.Small.E90 />
                    <Label.Medium.Light>
                        Please review information before sending
                    </Label.Medium.Light>
                    <Button.P50
                        onClick={() =>
                            void updateThisBooking({
                                reviewNeeded: false,
                            })
                        }
                    >
                        Review done <Icon.Check.Medium />
                    </Button.P50>
                </Flex.Row>
            ) : (
                <Button.Ghost
                    onClick={() => {
                        if (isImageRequired()) {
                            window.alert(`Please add at least one image`);
                            return false;
                        }
                        history.push(routes.request(request.id));
                        return false;
                    }}
                >
                    <Icon.Close.Medium />
                </Button.Ghost>
            )}
        </Flex.Row>
    );

    const bookingTitleInput = (
        <Input.TextArea
            className="textarea__heading"
            value={booking.title || ""}
            rows={1}
            placeholder="Booking option title"
            trim
            removeLineBreaks
            onBlur={(title) => updateThisBooking({ title })}
        />
    );
    const bookingImagesInput = (
        <Flex.Row className="w-full max-w-full">
            <EditBookingImages booking={booking} invalidInput={isImageRequired()} />
        </Flex.Row>
    );
    const contactDetailsSection = (
        <>
            <h4>Contact details</h4>
            <Flex.Row gap="large">
                <Input.TextField
                    label="Website"
                    placeholder="https://"
                    value={booking.website ?? ""}
                    isValid={(value) => isTypeOf(value, UrlString)}
                    onBlur={(website) => {
                        if (!isTypeOf(website, UrlString)) {
                            EventEmitter.emit({
                                name: "showToastMessage",
                                params: {
                                    message: `Website must be a valid URL or empty.`,
                                    type: "error",
                                },
                            });
                            return;
                        }

                        void updateThisBooking({ website });
                    }}
                />
                <Input.TextField
                    label="Phone"
                    placeholder="+1 123 456 7890"
                    value={booking.phoneNumber ?? ""}
                    onBlur={(phoneNumber) => updateThisBooking({ phoneNumber })}
                />
            </Flex.Row>
            <Flex.Row gap="large">
                <Input.TextField
                    label="Visible address"
                    placeholder="Enter address"
                    value={booking.address ?? ""}
                    onBlur={(address) => updateThisBooking({ address })}
                />
            </Flex.Row>
        </>
    );

    const descriptionSection = (
        <>
            <h4>Description</h4>
            <Input.TextField
                label="Snappy headline"
                value={booking.headline ?? ""}
                placeholder="A 3–10 word title describing what’s cool or defining about this place"
                onBlur={(headline) => updateThisBooking({ headline })}
            />
            <TiptapEditor
                label="Description"
                content={booking.description ?? ""}
                placeholder="One-to-three-paragraph description of what the place is like and who it is for"
                onBlur={(description) => {
                    void updateThisBooking({ description });
                }}
            />
            <Input.TextArea
                label="Albertine benefit"
                value={booking.benefits ?? ""}
                placeholder="Perks, benefits, or access provided by Albertine (“eg. early check-in subject to availability, “book with us for exclusive access”)"
                autoResize
                onBlur={(benefits) => updateThisBooking({ benefits })}
            />
        </>
    );

    const priceEnumSelection = (
        <>
            {booking.priceQuote === "final" ? (
                <Button.Ghost
                    onClick={() => {
                        void updateThisBooking({
                            priceQuote: null,
                            ...updateBookingPriceDetails(
                                booking.priceDetails,
                                null,
                                booking.paymentTo,
                            ),
                        });
                    }}
                >
                    <Tag.Medium.P90>
                        <FlexInline gap="small">
                            <Icon.Check.XSmall />
                            <span>Final</span>
                        </FlexInline>
                    </Tag.Medium.P90>
                </Button.Ghost>
            ) : (
                <Button.Ghost
                    onClick={() => {
                        void updateThisBooking({
                            priceQuote: "final",
                            ...updateBookingPriceDetails(
                                booking.priceDetails,
                                "final",
                                booking.paymentTo,
                            ),
                        });
                    }}
                >
                    <Tag.Medium.N80>Final</Tag.Medium.N80>
                </Button.Ghost>
            )}

            {booking.priceQuote === "estimate" ? (
                <Button.Ghost
                    onClick={() => {
                        void updateThisBooking({
                            priceQuote: null,
                            ...updateBookingPriceDetails(
                                booking.priceDetails,
                                null,
                                booking.paymentTo,
                            ),
                        });
                    }}
                >
                    <Tag.Medium.P90>
                        <FlexInline gap="small">
                            <Icon.Check.XSmall />
                            <span>Estimate</span>
                        </FlexInline>
                    </Tag.Medium.P90>
                </Button.Ghost>
            ) : (
                <Button.Ghost
                    onClick={() => {
                        void updateThisBooking({
                            priceQuote: "estimate",
                            ...updateBookingPriceDetails(
                                booking.priceDetails,
                                "estimate",
                                booking.paymentTo,
                            ),
                        });
                    }}
                >
                    <Tag.Medium.N80>Estimate</Tag.Medium.N80>
                </Button.Ghost>
            )}
        </>
    );

    const paymentEnumSelection = (
        <>
            {booking.paymentTo === "albertine" ? (
                <Button.Ghost
                    onClick={() => {
                        void updateThisBooking({
                            paymentTo: null,
                            ...updateBookingPriceDetails(
                                booking.priceDetails,
                                booking.priceQuote,
                                null,
                            ),
                        });
                    }}
                >
                    <Tag.Medium.P90>
                        <FlexInline gap="small">
                            <Icon.Check.XSmall />
                            <span>Member pays Albertine</span>
                        </FlexInline>
                    </Tag.Medium.P90>
                </Button.Ghost>
            ) : (
                <Button.Ghost
                    onClick={() => {
                        void updateThisBooking({
                            paymentTo: "albertine",
                            ...updateBookingPriceDetails(
                                booking.priceDetails,
                                booking.priceQuote,
                                "albertine",
                            ),
                        });
                    }}
                >
                    <Tag.Medium.N80>Member pays Albertine</Tag.Medium.N80>
                </Button.Ghost>
            )}

            {booking.paymentTo === "supplier" ? (
                <Button.Ghost
                    onClick={() => {
                        void updateThisBooking({
                            paymentTo: null,
                            ...updateBookingPriceDetails(
                                booking.priceDetails,
                                booking.priceQuote,
                                null,
                            ),
                        });
                    }}
                >
                    <Tag.Medium.P90>
                        <FlexInline gap="small">
                            <Icon.Check.XSmall />
                            <span>Member pays Supplier</span>
                        </FlexInline>
                    </Tag.Medium.P90>
                </Button.Ghost>
            ) : (
                <Button.Ghost
                    onClick={() => {
                        void updateThisBooking({
                            paymentTo: "supplier",
                            ...updateBookingPriceDetails(
                                booking.priceDetails,
                                booking.priceQuote,
                                "supplier",
                            ),
                        });
                    }}
                >
                    <Tag.Medium.N80>Member pays Supplier</Tag.Medium.N80>
                </Button.Ghost>
            )}
        </>
    );

    const bookingOptionCategory = isTypeOf(
        booking.offeringCategory,
        BookingOptionsAllowedCategory,
    )
        ? booking.offeringCategory
        : undefined;

    const showOptionsSection = !!bookingOptionCategory;

    const priceAndTermsSection = (
        <>
            <h4>Price and terms</h4>

            <Flex.Row gap="medium" className="edit-booking-form__price">
                <PriceInput
                    defaultCurrency={booking.price?.currency ?? booking.priceCurrency}
                    paymentsPreferredCurrency={memberPreferredCurrency}
                    price={booking.price}
                    onChange={(price) => {
                        void updateThisBooking({
                            price,
                            ...(price &&
                                price.currency !== booking.price?.currency && {
                                    priceCurrency: price.currency,
                                    options: booking.options
                                        ? booking.options.map((option) => ({
                                              ...option,
                                              price: option.price
                                                  ? {
                                                        ...option.price,
                                                        currency: price.currency,
                                                    }
                                                  : undefined,
                                          }))
                                        : undefined,
                                }),
                        });
                    }}
                    onChangeCurrency={updateThisBookingCurrency}
                />
            </Flex.Row>

            {memberPreferredCurrency && (
                <Flex.Row gap="medium">
                    <Tooltip
                        placement="top-left"
                        label="Preferred payment currency can be edited under member profile and payments"
                    >
                        <Icon.Info.Small.N20 />
                    </Tooltip>
                    <Label.Small.N50>{memberPreferredCurrency}</Label.Small.N50>
                </Flex.Row>
            )}

            <Flex.Row gap="medium" className="edit-booking-form__price-quote">
                <h6>Price is</h6>
                {priceEnumSelection}
            </Flex.Row>

            <Flex.Row gap="medium" className="edit-booking-form__payment-to">
                <h6>Payment to</h6>
                {paymentEnumSelection}
            </Flex.Row>

            <Flex.Column gap="small">
                <Input.TextArea
                    label="Price details"
                    value={booking.priceDetails ?? ""}
                    placeholder="Price breakdown or additional details if needed"
                    autoResize
                    onBlur={(priceDetails) => updateThisBooking({ priceDetails })}
                />
            </Flex.Column>

            <Input.TextArea
                label="Booking policy"
                value={booking.bookingPolicy ?? ""}
                placeholder="Is there a deposit? Can I cancel? Any other T&C’s the customer should know?"
                autoResize
                onBlur={(bookingPolicy) => updateThisBooking({ bookingPolicy })}
            />

            <Lib.Flex.Column gap="value8">
                {payments.map((payment) => (
                    <PaymentItem
                        key={payment.id}
                        payment={payment}
                        onChargeComplete={() => {
                            void refetchPayments();
                        }}
                    />
                ))}

                {isLoadingPayments && <LoadingSpinner />}
            </Lib.Flex.Column>

            {payments.length > 0 && (
                <TotalPaymentsRequested booking={booking} payments={payments} />
            )}

            <Tooltip
                label="Select price currency first"
                placement="right"
                hide={!!bookingCurrency}
            >
                <Button.N10
                    label="Request payment or commission"
                    disabled={!bookingCurrency}
                    onClick={() => {
                        setShowRequestPaymentModal(true);
                    }}
                />
            </Tooltip>
            {showRequestPaymentModal && (
                <RequestPaymentModal
                    booking={{
                        ...booking,
                        priceCurrency: bookingCurrency,
                    }}
                    request={request}
                    member={member}
                    previousPayments={payments}
                    onPaymentChanged={() => {
                        void refetchPayments();
                    }}
                    onClose={(shouldRefetch) => {
                        if (shouldRefetch) {
                            void refetchPayments();
                        }

                        setShowRequestPaymentModal(false);
                    }}
                />
            )}
        </>
    );

    const personalNoteSection = (
        <>
            <h4>Notes from the team</h4>
            <Flex.Row gap="large" className="edit-booking-form__personal-note">
                <Input.TextArea
                    value={booking.notesForMember ?? ""}
                    placeholder="What's special about the place?"
                    rows={3}
                    autoResize
                    onBlur={(value) => {
                        void updateThisBooking({
                            notesForMember: value,
                        });
                    }}
                />
            </Flex.Row>
        </>
    );

    const removeAttachment = async (attachmentToRemove: BookingAttachment) => {
        if (!booking) return;

        try {
            await updateThisBookingAttachments(
                (booking.attachments ?? []).filter(
                    (it) => it.id !== attachmentToRemove.id,
                ),
            );
            if (attachmentToRemove.storageName) {
                const attachmentRef = bookingAttachmentRef(
                    attachmentToRemove.storageName,
                    booking.id,
                    booking.member,
                );
                await deleteFile(attachmentRef);
            }
        } catch (error) {
            window.alert(error);
        }
    };

    const renameAttachment = async (attachmentToRename: BookingAttachment) => {
        if (!booking) return;
        await updateThisBookingAttachments(
            (booking.attachments ?? []).map((it) => {
                if (it.id === attachmentToRename.id) {
                    return {
                        ...it,
                        filename: attachmentToRename.filename,
                    };
                }
                return it;
            }),
        );
    };

    const changeAttachmentPrivacy = async (
        attachmentToChange: BookingAttachment,
        isPublic: boolean,
    ) => {
        if (!booking) return;
        await updateThisBookingAttachments(
            (booking.attachments ?? []).map((it) => {
                if (it.id === attachmentToChange.id) {
                    return isPublic ? makePublic(it) : makePrivate(it);
                }
                return it;
            }),
        );
    };

    const attachmentsSection = (
        <>
            <h4>Attachments</h4>
            <Flex.Column gap="medium">
                <Paragraph.Medium.Light.N10>
                    Ensure attachments with personal info or booking confirmations are
                    private; others should be public.
                </Paragraph.Medium.Light.N10>

                {booking.attachments?.map((attachment) => (
                    <BookingAttachmentPreview
                        key={attachment.id}
                        attachment={attachment}
                        onRemove={removeAttachment}
                        onRename={renameAttachment}
                        onChangePrivacy={changeAttachmentPrivacy}
                    />
                ))}
                {booking.reviewNeeded ? (
                    <Label.Medium.N50>
                        Please review booking information before adding attachments
                    </Label.Medium.N50>
                ) : (
                    <HandleFileUpload
                        dragAndDropLabel="Drag and drop attachments here..."
                        onUpload={(files) => void uploadAttachments(files)}
                        className="edit-booking-form__attachments"
                    >
                        {uploadingAttachments ? (
                            <div className="edit-booking-form__attachments-placeholder">
                                Uploading...
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <div className="edit-booking-form__attachments-placeholder">
                                Drag and drop or click to add attachments
                            </div>
                        )}
                    </HandleFileUpload>
                )}
            </Flex.Column>
        </>
    );

    const archiveThisBooking = async () => {
        if (!window.confirm("Archive option?")) {
            return;
        }
        try {
            await archiveBookingMutation({
                param: { bookingId: booking.id },
            });
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "info",
                    message: "Option successfully archived",
                },
            });
            history.push(routes.request(request.id));
        } catch {
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "error",
                    message: "Archiving option failed",
                },
            });
        }
    };

    const actionsSection = (
        <>
            <h4>Actions</h4>
            <Flex.Row gap="large">
                <Button.E90 onClick={() => void archiveThisBooking()}>
                    <Icon.Delete.Medium /> Archive option
                </Button.E90>
            </Flex.Row>
        </>
    );

    const supplierOptions = suppliers.map((it) => ({
        id: it.id,
        text: it.name,
        selected: (booking.suppliers ?? []).includes(it.id),
    }));

    const supplierSection = (
        <Flex.Column gap="large" className="edit-booking-form__right__panel">
            <Flex.Row justifyContent="space-between">
                <h4>Supplier info</h4>
                <Tooltip label="Internal to Albertine" placement="top-right">
                    <Icon.VpnKey.Medium />
                </Tooltip>
            </Flex.Row>
            <AutocompleteDropdown
                label="Suppliers"
                items={supplierOptions}
                onChange={(newSuppliers) => {
                    void updateThisBooking({
                        suppliers: newSuppliers.map((it) => it.id),
                    });
                }}
            />
            <Input.TextArea
                value={booking.supplierDetails ?? ""}
                label="Supplier details"
                placeholder="What should the team know? Who is our contact? Where did you book?"
                rows={3}
                autoResize
                onBlur={(supplierDetails) => updateThisBooking({ supplierDetails })}
                isValid={(value) => value.trim() !== ""}
            />
        </Flex.Column>
    );
    return (
        <>
            <FullScreenOverlay>
                <Flex.Column className="edit-booking-form">
                    {headerRow}
                    <Flex.Row className="edit-booking-form__container">
                        <Flex.Column
                            gap="large"
                            className="edit-booking-form__scrollable-pane"
                        >
                            <Flex.Column
                                gap="large"
                                className="edit-booking-form__left"
                            >
                                {bookingTitleInput}
                                {bookingImagesInput}
                                {contactDetailsSection}
                                <hr />
                                {descriptionSection}
                                {showOptionsSection && (
                                    <>
                                        <hr />
                                        <BookingOptions
                                            booking={booking}
                                            updateThisBooking={updateThisBooking}
                                            updateThisBookingCurrency={
                                                updateThisBookingCurrency
                                            }
                                            setShowChooseBookingOptionOverlay={
                                                setShowChooseBookingOptionOverlay
                                            }
                                        />
                                    </>
                                )}
                                <hr />
                                {priceAndTermsSection}
                                <hr />
                                {attachmentsSection}
                                <hr />
                                {personalNoteSection}
                                <hr />
                                {actionsSection}
                            </Flex.Column>
                        </Flex.Column>
                        <Flex.Column
                            gap="small"
                            className="edit-booking-form__scrollable-pane edit-booking-form__right"
                        >
                            <BookingDetailsPanel
                                booking={booking}
                                request={request}
                                onUpdate={updateThisBooking}
                                displayShowChooseBookingOptionOverlay={() => {
                                    setShowChooseBookingOptionOverlay(true);
                                }}
                            />
                            {supplierSection}
                        </Flex.Column>
                    </Flex.Row>
                </Flex.Column>
            </FullScreenOverlay>
            {showChooseBookingOptionOverlay && (
                <BookingOptionsSelectionOverlay
                    booking={booking}
                    onClose={() => {
                        setShowChooseBookingOptionOverlay(false);
                    }}
                    onConfirm={(selected) => {
                        const options = booking.options;
                        if (!options) return;
                        void (async () => {
                            await updateThisBooking({
                                options: options.map((option) => ({
                                    ...option,
                                    confirmed: selected.includes(option.id),
                                })),
                            });
                            await changeBookingStatus({
                                param: { bookingId: booking.id },
                                json: { status: "confirmed" },
                            });
                        })();
                        setShowChooseBookingOptionOverlay(false);
                    }}
                />
            )}
        </>
    );
}
